import React, { useState } from "react";

import { Subscription } from "../../api/grpc/subscription/subscription";
import { useSubscriptionRequests } from "../../api/grpc/subscription/useSubscriptionRequests";
import { usePartnerData } from "../../components/Billings/lib/usePartnerData";
import { ContactSalesModal } from "../../components/ContactSales/ContactSalesModal";
import { ProductStoreCartDialog } from "../../components/ProductStore/ProductStoreCartDialog";
import { ProductStoreCatalog } from "../../components/ProductStore/ProductStoreCatalog";
import { ProductStoreCheckoutDialog } from "../../components/ProductStore/ProductStoreCheckoutDialog";
import { ProductStoreHeader } from "../../components/ProductStore/ProductStoreHeader";
import {
  ProductStoreAction,
  ProductStorePricing,
} from "../../components/ProductStore/ProductStore.types";
import {
  CartSummary,
  getCheckoutRoute,
} from "../../components/ProductStore/productStoreCart";
import {
  getPricingMode,
  getQuoteRequestMessage,
} from "../../components/ProductStore/productStorePricing";
import {
  PurchaseOffer,
  findPurchaseOffer,
} from "../../components/ProductStore/productStorePurchase";
import { getSubscriptionTerm } from "../../components/ProductStore/productStoreSubscription";
import { findSubscriptionAddOn } from "../../components/ProductStore/productStoreSubscriptionAddOn";
import { useProductStorePrices } from "../../components/ProductStore/useProductStorePrices";
import { useSubscriptionAddOnCheckout } from "../../components/ProductStore/useSubscriptionAddOnCheckout";
import { createCartCheckout } from "../../components/Subscription/lib/createCartCheckout";
import { createOneTimeCheckout } from "../../components/Subscription/lib/createOneTimeCheckout";
import { createSubscriptionCheckout } from "../../components/Subscription/lib/createSubscriptionCheckout";
import { useAuthContext } from "../../lib/context/Auth/AuthContext";
import useGrpcQuery from "../../lib/hooks/useGrpcQuery";
import { useRouter } from "../../lib/hooks/useRouter";
import { useToggleModal } from "../../lib/hooks/useToggleModal";

import "./ProductStore.scss";

const BILLING_PATH = "/settings/billing/subscriptions";

export const ProductStore = () => {
  const router = useRouter();
  const { user, tokenInterceptor } = useAuthContext();
  const { isOpen, toggleModal } = useToggleModal();
  const [contactSubject, setContactSubject] = useState<string>();
  const { getCustomerSubscription } = useSubscriptionRequests();
  // One request, shared by the header summary and term-priced add-ons.
  const subscription = useGrpcQuery<{}, Subscription>({
    method: getCustomerSubscription,
    variables: {},
  });
  const {
    addOn,
    ownedAddOns,
    open: openAddOn,
    close: closeAddOn,
    createCheckout: createAddOnCheckout,
    complete: completeAddOn,
    markAdded,
  } = useSubscriptionAddOnCheckout(subscription);
  const pricing: ProductStorePricing = {
    mode: getPricingMode(usePartnerData()),
    prices: useProductStorePrices(),
    term: subscription.data
      ? getSubscriptionTerm(subscription.data)
      : undefined,
    subscriptionAddOns: ownedAddOns,
  };
  // What the order dialog offers, captured when "Order now" opened it.
  const [cartOffer, setCartOffer] = useState<PurchaseOffer>();

  const openContactForm = (subject?: string) => {
    setContactSubject(subject);
    toggleModal();
  };

  const handleAction = (action: ProductStoreAction) => {
    if (action.type === "checkout" || action.type === "subscription") {
      const offer = findPurchaseOffer(
        action.productType,
        pricing,
        subscription.data
      );

      if (offer) {
        setCartOffer(offer);
        return;
      }

      if (action.type === "checkout") {
        openContactForm();
        return;
      }

      // An add-on bought on its own, e.g. Total Care from its card.
      const subscriptionAddOn =
        subscription.data && findSubscriptionAddOn(action.productType, pricing);

      if (subscriptionAddOn) {
        openAddOn(subscriptionAddOn);
      } else {
        router.push(BILLING_PATH);
      }
      return;
    }

    if (action.type === "billing") {
      router.push(BILLING_PATH);
      return;
    }

    if (action.type === "contact") {
      openContactForm(action.subject);
      return;
    }

    const product = document.getElementById(action.targetId);
    product?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.requestAnimationFrame(() => product?.focus({ preventScroll: true }));
  };

  // CreateCartCheckout needs an add-on and CreateOneTimeCheckout can't take
  // one, so the cart's contents decide the call.
  const checkoutCart = (summary: CartSummary) => {
    const customerID = user?.customerid || "";
    const route = getCheckoutRoute(summary);

    if (route === "oneTime") {
      return createOneTimeCheckout(customerID, tokenInterceptor, summary.items);
    }

    if (route === "subscription") {
      return createSubscriptionCheckout(
        customerID,
        subscription.data?.id || "",
        tokenInterceptor,
        summary.items
      );
    }

    return createCartCheckout(customerID, tokenInterceptor, summary.items);
  };

  return (
    <main className="ProductStore default-page">
      <ProductStoreHeader
        pricingMode={pricing.mode}
        subscription={subscription}
      />
      <ProductStoreCatalog pricing={pricing} onAction={handleAction} />
      {cartOffer && (
        <ProductStoreCartDialog
          offer={cartOffer}
          createCheckout={checkoutCart}
          onClose={() => setCartOffer(undefined)}
          onComplete={({ subscription: added }) =>
            markAdded(
              added.flatMap(({ entityType }) =>
                entityType === undefined ? [] : [entityType]
              )
            )
          }
        />
      )}
      {addOn && (
        <ProductStoreCheckoutDialog
          title={`Add ${addOn.title}`}
          createCheckout={() => createAddOnCheckout(addOn)}
          onClose={closeAddOn}
          onComplete={() => completeAddOn(addOn)}
        />
      )}
      {isOpen && (
        <ContactSalesModal
          isOpen={isOpen}
          toggle={toggleModal}
          initialMessage={getQuoteRequestMessage(contactSubject)}
        />
      )}
    </main>
  );
};
